import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Field, Icon, Input, Select, Textarea } from "@sagot/ui";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { FORM_FIELD_TYPES, FORM_LIMITS, hasChoices, type BookingFormValues } from "./booking-form-model";

const P = "build.actions.bookingForm";
const OPTIONS_ROWS = 4;

interface BookingFormFieldRowProps {
  index: number;
  count: number;
  disabled: boolean;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}

/** One custom field of the booking form: question, type, required, choices, order and removal. */
export function BookingFormFieldRow({ index, count, disabled, onMove, onRemove }: BookingFormFieldRowProps) {
  const { t } = useTranslation();
  const form = useFormContext<BookingFormValues>();
  const type = useWatch({ control: form.control, name: `fields.${index}.type` });
  const label = useWatch({ control: form.control, name: `fields.${index}.label` });
  const errors = form.formState.errors.fields?.[index];
  const name = label.trim() || t(`${P}.fieldN`, { n: index + 1 });
  return (
    <li className="space-y-3 rounded-md border border-line p-3" data-testid="booking-form-field">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_10rem]">
        <Field label={t(`${P}.label`)} error={errors?.label?.message}>
          <Input maxLength={FORM_LIMITS.label} placeholder={t(`${P}.labelPlaceholder`)} disabled={disabled} {...form.register(`fields.${index}.label`)} />
        </Field>
        <Field label={t(`${P}.type`)}>
          <Select disabled={disabled} {...form.register(`fields.${index}.type`)}>
            {FORM_FIELD_TYPES.map((value) => <option key={value} value={value}>{t(`${P}.types.${value}`)}</option>)}
          </Select>
        </Field>
      </div>
      {hasChoices(type) ? (
        <Field label={t(`${P}.options`)} hint={t(`${P}.optionsHint`)} error={errors?.optionsText?.message}>
          <Textarea rows={OPTIONS_ROWS} placeholder={t(`${P}.optionsPlaceholder`)} disabled={disabled} {...form.register(`fields.${index}.optionsText`)} />
        </Field>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex min-h-11 items-center gap-2 text-sm font-semibold">
          <Controller control={form.control} name={`fields.${index}.required`} render={({ field }) => (
            <Checkbox checked={field.value} disabled={disabled} onCheckedChange={(value) => { field.onChange(value === true); }} />
          )} />
          {t(`${P}.required`)}
        </label>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || index === 0} aria-label={t(`${P}.moveUp`, { name })} onClick={() => { onMove(index, index - 1); }}>
            <Icon name="chevron-up" />
          </Button>
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || index === count - 1} aria-label={t(`${P}.moveDown`, { name })} onClick={() => { onMove(index, index + 1); }}>
            <Icon name="chevron-down" />
          </Button>
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t(`${P}.remove`, { name })} onClick={() => { onRemove(index); }}>
            <Icon name="trash" />
          </Button>
        </div>
      </div>
    </li>
  );
}
